import { AppDispatch } from "../store";
import { setNotification } from "../store/toastNotifications";
import {
  setEmployees,
  setClients,
  setCategories,
  addCategory,
  updateCategory,
  removeCategory,
} from "../store/constraint";
import {
  AddEmployeeConstraintBody,
  AddClientConstraintBody,
  EditEmployeeConstraintBody,
  CategoryImpact,
  ConcernCategory,
  Constraint,
} from "../types/constraint";
import { Client, ConstraintEmployee } from "../types/ganttChart";
import api, { API_PATHS } from "./api";
import labels from "../utils/labels";

type ConflictBody = {
  message?: string;
  clashingName?: string;
};

const isConflictError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 409;

// Older backends send a plain string body with no clashingName; falls back to undefined.
const getClashingName = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data !== "object" || data === null) {
    return undefined;
  }
  const { clashingName } = data as ConflictBody;
  return typeof clashingName === "string" && clashingName.trim() !== ""
    ? clashingName
    : undefined;
};

const categoryNameTakenMessage = (error: unknown): string => {
  const clashingName = getClashingName(error);
  return clashingName
    ? labels.errorCategoryNameTakenWithName(clashingName)
    : labels.errorCategoryNameTaken;
};

const constraintExistsMessage = (error: unknown): string => {
  const clashingName = getClashingName(error);
  return clashingName
    ? labels.errorConstraintAlreadyExistsWithName(clashingName)
    : labels.errorConstraintAlreadyExists;
};

class ConstraintService {
  static async getEmployees(dispatch: AppDispatch): Promise<void> {
    try {
      const response = await api.get<{
        employeeConstraints: ConstraintEmployee[];
      }>(API_PATHS.getEmployeeConstraints);
      dispatch(setEmployees(response.data.employeeConstraints));
    } catch (error) {
      dispatch(setNotification({ message: labels.errorFetchEmployees }));
      throw error;
    }
  }

  static async getCategories(dispatch: AppDispatch): Promise<void> {
    try {
      const response = await api.get<{ concernCategories: ConcernCategory[] }>(
        API_PATHS.getConcernCategories,
      );
      dispatch(setCategories(response.data.concernCategories));
    } catch (error) {
      dispatch(setNotification({ message: labels.errorFetchCategories }));
      throw error;
    }
  }

  static async getClients(dispatch: AppDispatch): Promise<void> {
    try {
      const response = await api.get<{ serviceClientConstraints: Client[] }>(
        API_PATHS.getClientConstraints,
      );
      dispatch(setClients(response.data.serviceClientConstraints));
    } catch (error) {
      dispatch(setNotification({ message: labels.errorFetchClients }));
      throw error;
    }
  }

  static async addEmployeeConstraint(
    body: AddEmployeeConstraintBody,
    dispatch: AppDispatch,
  ): Promise<Constraint> {
    try {
      const response = await api.post<Constraint>(
        API_PATHS.addEmployeeConstraint,
        body,
      );
      return response.data;
    } catch (error) {
      const isConflict = isConflictError(error);
      if (isConflict) {
        // The planner's employee list is stale — someone else added this hensyn, or their own
        // copy simply predates it — so refetch before rethrowing to reflect the real current
        // state and let the dropdown correctly hide the target next time.
        await ConstraintService.getEmployees(dispatch).catch(() => {});
      }
      dispatch(
        setNotification({
          message: isConflict
            ? constraintExistsMessage(error)
            : labels.errorSaveConstraint,
        }),
      );
      throw error;
    }
  }

  static async editEmployeeConstraint(
    body: EditEmployeeConstraintBody,
    dispatch: AppDispatch,
  ): Promise<Constraint> {
    try {
      const response = await api.post<Constraint>(
        API_PATHS.editEmployeeConstraint,
        body,
      );
      return response.data;
    } catch (error) {
      const isConflict = isConflictError(error);
      if (isConflict) {
        await ConstraintService.getEmployees(dispatch).catch(() => {});
      }
      dispatch(
        setNotification({
          message: isConflict
            ? constraintExistsMessage(error)
            : labels.errorUpdateConstraint,
        }),
      );
      throw error;
    }
  }

  static async addClientConstraint(
    body: AddClientConstraintBody,
    dispatch: AppDispatch,
  ): Promise<Constraint> {
    try {
      const response = await api.post<Constraint>(
        API_PATHS.addClientConstraint,
        body,
      );
      return response.data;
    } catch (error) {
      const isConflict = isConflictError(error);
      if (isConflict) {
        // Same reasoning as addEmployeeConstraint: refetch on conflict only, so a stale client
        // list doesn't keep offering a target that is already taken.
        await ConstraintService.getClients(dispatch).catch(() => {});
      }
      dispatch(
        setNotification({
          message: isConflict
            ? constraintExistsMessage(error)
            : labels.errorSaveConstraint,
        }),
      );
      throw error;
    }
  }

  static async addConcernCategory(
    name: string,
    dispatch: AppDispatch,
  ): Promise<ConcernCategory> {
    try {
      const response = await api.post<ConcernCategory>(
        API_PATHS.addConcernCategory,
        { name },
      );
      dispatch(addCategory(response.data));
      return response.data;
    } catch (error) {
      dispatch(
        setNotification({
          message: isConflictError(error)
            ? categoryNameTakenMessage(error)
            : labels.errorSaveCategory,
        }),
      );
      throw error;
    }
  }

  static async editConcernCategory(
    id: string,
    name: string,
    dispatch: AppDispatch,
  ): Promise<void> {
    try {
      await api.post(API_PATHS.editConcernCategory, { id, name });
      dispatch(updateCategory({ id, name }));
    } catch (error) {
      dispatch(
        setNotification({
          message: isConflictError(error)
            ? categoryNameTakenMessage(error)
            : labels.errorUpdateCategory,
        }),
      );
      throw error;
    }
  }

  static async deleteConcernCategory(
    id: string,
    dispatch: AppDispatch,
  ): Promise<void> {
    try {
      await api.post(API_PATHS.deleteConcernCategory, { id });
      dispatch(removeCategory(id));
      // Category delete is soft on the backend — employees and clients that referenced it
      // need to be refetched so the removal is reflected in their constraint lists.
      await Promise.all([
        ConstraintService.getEmployees(dispatch).catch(() => {}),
        ConstraintService.getClients(dispatch).catch(() => {}),
      ]);
    } catch (error: any) {
      dispatch(setNotification({ message: labels.errorDeleteCategory }));
      throw error;
    }
  }

  /**
   * Unlike every other method here, a failed fetch dispatches no notification: the delete
   * confirmation dialog already shows a client-derived count instantly, and this is only meant
   * to replace it with the authoritative one when it lands. The caller (useCategoryImpact)
   * catches and ignores a rejection, leaving the derived count on screen rather than surfacing
   * an error the planner can't act on.
   */
  static async getCategoryImpact(
    id: string,
    dispatch: AppDispatch,
  ): Promise<CategoryImpact> {
    const response = await api.get<CategoryImpact>(
      API_PATHS.getConcernCategoryImpact,
      { params: { id } },
    );
    return response.data;
  }

  static async deleteEmployeeConstraint(
    constraintId: number,
    isCategoryConstraint: boolean,
    dispatch: AppDispatch,
  ): Promise<void> {
    try {
      await api.post(API_PATHS.deleteEmployeeConstraint, {
        id: constraintId,
        isCategoryConstraint,
      });
    } catch (error: any) {
      dispatch(setNotification({ message: labels.errorDeleteConstraint }));
      throw error;
    }
  }

  static async deleteClientConstraint(
    constraintId: number,
    dispatch: AppDispatch,
  ): Promise<void> {
    try {
      await api.post(API_PATHS.deleteClientConstraint, { id: constraintId });
    } catch (error: any) {
      dispatch(setNotification({ message: labels.errorDeleteConstraint }));
      throw error;
    }
  }
}

export default ConstraintService;
